import { useSeo } from '../lib/seo';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowUpRight, Mail, Ticket } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../lib/api';
import { CriteriaAccordion } from './TradeSubPages';

// Defence-in-depth: admin-authored fields may contain entities (&amp;) but
// must never carry elements or event handlers.
const stripTags = (s) => (s || '').replace(/<[^>]*>/g, '');

// Short, scannable labels for sibling-page tabs. We keep these in code so
// admins editing page titles can still rely on a clean tab strip.
const TAB_LABELS = {
  'great-days-out': 'Visitor Guide',
  'stayplayexplore': 'StayPlayExplore',
  'tourism-awards': 'Overview',
  'awards-criteria': 'Eligibility & criteria',
  'awards-tcs': 'Terms & conditions',
  'awards-night': '2025 winners',
};

const CATEGORY_LABELS = {
  awards: 'Tourism Awards',
  marketing: 'Marketing channels',
  support: 'Advice & support',
};

function SiblingTabs({ category, currentSlug, siblings }) {
  if (!siblings || siblings.length < 2) return null;
  return (
    <section className="bg-stone border-b border-charcoal/10 sticky top-[64px] z-20" data-testid={`sibling-tabs-${category}`}>
      <div className="max-w-[900px] mx-auto px-6 lg:px-12">
        <div className="flex items-center gap-1 overflow-x-auto py-2 -mb-px">
          {CATEGORY_LABELS[category] && (
            <span className="hidden md:inline label text-charcoal/55 mr-3 shrink-0 text-[10px]">{CATEGORY_LABELS[category]}</span>
          )}
          {siblings.map((s) => {
            const active = s.slug === currentSlug;
            const label = TAB_LABELS[s.slug] || s.title;
            return (
              <Link
                key={s.slug}
                to={`/partners/${s.slug}`}
                data-testid={`sibling-tab-${s.slug}`}
                aria-current={active ? 'page' : undefined}
                className={`shrink-0 inline-flex items-center text-sm font-semibold rounded-full px-4 py-2 transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-heritage text-base'
                    : 'text-charcoal/70 hover:text-heritage hover:bg-base'
                }`}
              >
                {label}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const MARKDOWN_COMPONENTS = {
  h2: (props) => <h2 className="font-display text-2xl sm:text-3xl text-charcoal mt-12 mb-4 first:mt-0" {...props} />,
  h3: (props) => <h3 className="font-semibold text-lg text-charcoal mt-8 mb-2" {...props} />,
  h4: (props) => <h4 className="font-semibold text-base text-charcoal mt-6 mb-2" {...props} />,
  p: (props) => <p className="mb-4 text-charcoal/80 leading-relaxed" {...props} />,
  ul: (props) => <ul className="mb-5 ml-5 list-disc text-charcoal/80 space-y-1.5" {...props} />,
  ol: (props) => <ol className="mb-5 ml-5 list-decimal text-charcoal/80 space-y-1.5" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  a: ({ href, children, ...rest }) => {
    const external = href && /^https?:\/\//i.test(href);
    if (external) {
      return <a href={href} target="_blank" rel="noopener noreferrer" className="text-heritage underline hover:text-charcoal" {...rest}>{children}</a>;
    }
    return <a href={href} className="text-heritage underline hover:text-charcoal" {...rest}>{children}</a>;
  },
  strong: (props) => <strong className="text-charcoal font-semibold" {...props} />,
  em: (props) => <em className="italic text-charcoal/75" {...props} />,
  hr: () => <hr className="my-10 border-charcoal/15" />,
  blockquote: (props) => <blockquote className="border-l-2 border-heritage pl-4 text-charcoal/70 italic my-6" {...props} />,
  code: (props) => <code className="bg-stone px-1.5 py-0.5 rounded text-sm" {...props} />,
};

/**
 * DB-backed trade sub-page. The slug comes from either the URL param or
 * a `slug` prop (so callers can also mount it directly). Content is fetched
 * from /api/trade-pages/{slug} and rendered as markdown inside the same
 * visual shell partners expect from the hand-coded TradeSubPage.
 *
 * Admin edits via /admin/trade-pages are reflected here on the next page
 * load — no rebuild required.
 */
export default function TradePageView({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [page, setPage] = useState(null);
  useSeo(
    page?.title ? `${page.title} | Trade | Go Leicestershire` : null,
    page?.intro?.slice(0, 160)
  );
  const [siblings, setSiblings] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setErr('');
    (async () => {
      try {
        const { data } = await api.get(`/trade-pages/${slug}`);
        if (cancelled) return;
        setPage(data);
        // Fetch sibling pages in the same category (powers the in-page
        // tab bar — e.g. all 4 awards pages share a tab nav).
        if (data.category && data.category !== 'other') {
          try {
            const { data: sibData } = await api.get('/trade-pages', { params: { category: data.category } });
            if (!cancelled) setSiblings(sibData.items || []);
          } catch (e) {
            // siblings are nice-to-have, swallow errors silently
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e.response?.status === 404 ? 'Page not found.' : 'Could not load this page right now.');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (err) {
    return (
      <div className="bg-base min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center text-charcoal/65">
          <p className="mb-4">{err}</p>
          <Link to="/partners" className="text-heritage underline">Back to Trade Partners</Link>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="bg-base min-h-[60vh] flex items-center justify-center" data-testid={`trade-page-loading-${slug}`}>
        <div className="text-sm text-charcoal/55">Loading…</div>
      </div>
    );
  }

  const showCta = !!page.cta_href;
  const showContact = !!page.contact_email;
  const showBuyTickets = page.category === 'awards' && ['tourism-awards', 'awards-night'].includes(page.slug);

  return (
    <div className="bg-base min-h-screen" data-testid={`trade-page-${page.slug}`}>
      <Helmet>
        <title>{`${page.title} — GoLeicestershire Trade Partners`}</title>
        <meta name="description" content={page.intro?.slice(0, 160) || ''} />
      </Helmet>

      {/* Hero */}
      <section className="bg-heritage text-base py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative">
          <Link to="/partners" className="inline-flex items-center gap-2 text-base/80 hover:text-mustard text-sm mb-6" data-testid="back-to-partners">
            <ArrowLeft size={14} /> Trade Partners
          </Link>
          {page.label && <div className="label text-mustard mb-3" dangerouslySetInnerHTML={{ __html: stripTags(page.label) }} />}
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-4" dangerouslySetInnerHTML={{ __html: stripTags(page.title) }} />
          {page.intro && (
            <p className="text-base/85 text-lg max-w-2xl leading-relaxed" dangerouslySetInnerHTML={{ __html: stripTags(page.intro) }} />
          )}
        </div>
      </section>

      {/* Cover image (optional) */}
      {page.cover_image_url && (
        <section className="bg-base">
          <div className="max-w-[900px] mx-auto px-6 lg:px-12 -mt-10 md:-mt-14 relative z-10">
            <img
              src={page.cover_image_url}
              alt={page.title}
              className="w-full max-h-[480px] object-contain rounded-2xl border border-charcoal/10 bg-white shadow-lg"
              data-testid="trade-page-cover-image"
            />
          </div>
        </section>
      )}

      {/* Sibling-page tabs (e.g. all awards pages share an in-page tab nav) */}
      <SiblingTabs category={page.category} currentSlug={page.slug} siblings={siblings} />

      {/* Body — markdown rendered with friendly typography */}
      <section className="max-w-[820px] mx-auto px-6 lg:px-12 py-12 md:py-16">
        <article className="trade-markdown text-charcoal/85 leading-relaxed">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
            {page.body_md || ''}
          </ReactMarkdown>
        </article>

        {/* Awards hub: on-site category criteria so partners don't need the
            external portal just to read eligibility/judging rules */}
        {page.slug === 'tourism-awards' && <div className="mt-12"><CriteriaAccordion /></div>}

        {/* CTA + contact strip */}
        {(showCta || showContact || showBuyTickets) && (
          <div className="bg-stone border border-charcoal/10 rounded-2xl p-6 md:p-8 mt-12 flex flex-wrap items-center gap-4">
            {showBuyTickets && (
              <Link
                to="/awards/tickets"
                data-testid="buy-tickets-btn"
                className="inline-flex items-center gap-2 bg-heritage text-base font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-charcoal transition-colors"
              >
                <Ticket size={14} /> Book ceremony tickets
              </Link>
            )}
            {showCta && (
              <a
                href={page.cta_href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="primary-cta"
                className={`inline-flex items-center gap-2 font-semibold rounded-full px-5 py-2.5 text-sm transition-colors ${showBuyTickets ? 'border border-heritage text-heritage hover:bg-heritage hover:text-base' : 'bg-heritage text-base hover:bg-charcoal'}`}
              >
                {page.cta_label || 'Open link'} <ArrowUpRight size={14} />
              </a>
            )}
            {showContact && (
              <a
                href={`mailto:${page.contact_email}?subject=${encodeURIComponent(page.contact_cta || page.title)}`}
                data-testid="contact-email"
                className="inline-flex items-center gap-2 text-heritage font-semibold text-sm hover:underline"
              >
                <Mail size={14} /> {page.contact_cta || `Email ${page.contact_email}`} <ArrowUpRight size={12} />
              </a>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
